import React from 'react';
import { 
    Zap, Activity, Target, MessageSquare, Lightbulb, Heart, TrendingUp, Share2, 
    Eye, Mail, MousePointer2, Megaphone, BrainCircuit, Timer, FileText, 
    BarChart3, AlertTriangle, CheckCircle2, Rocket, Repeat
} from "lucide-react";
import { RenderStrategicGaps } from "../DanvinciHelpers";

// --- AESTHETIC HELPERS ---
const GlassContainer = ({ children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl overflow-hidden ${className}`}>
        {children}
    </div>
);

export const PersuasionTab = ({ persuasionMetrics, messageConsistency, recommendationsList, strategicGaps, kineticStrategy }) => {
    
    // Safety check
    if (!recommendationsList) return null;

    // --- 1. DATA SYNTHESIS: Engagement Hooks ---
    const avgStriatum = recommendationsList.reduce((acc, r) => acc + (r.predicted_neural_arousal?.striatum_reward_prediction || 0), 0) / (recommendationsList.length || 1);
    const avgInsula = recommendationsList.reduce((acc, r) => acc + (r.predicted_neural_arousal?.insula_ACC_predicted_arousal || 0), 0) / (recommendationsList.length || 1);
    const avgDwell = recommendationsList.reduce((acc, r) => acc + (r.visual_rhetoric_analysis?.attention_capture_analysis?.estimated_dwell_time_seconds || 0), 0) / (recommendationsList.length || 1);

    const getEngagementHook = (reward, pain) => {
        if (reward > 0.6) return { title: "Hope & Reward", desc: "Voters are responding to the promise of a better future. Keep messaging positive and aspirational.", icon: Zap, color: "text-amber-400" };
        if (pain > 0.15) return { title: "Urgency & Threat", desc: "Voters are responding to risk aversion. Highlight what is at stake if they DON'T vote.", icon: AlertTriangle, color: "text-rose-400" };
        return { title: "Duty & Norms", desc: "Engagement is driven by civic duty rather than intense emotion. Use 'Join your neighbors' framing.", icon: CheckCircle2, color: "text-blue-400" };
    };

    const hook = getEngagementHook(avgStriatum, avgInsula);

    // --- 2. DATA SYNTHESIS: Viral Physics (New) ---
    const getViralMetrics = () => {
        const viralScore = recommendationsList.reduce((acc, r) => acc + (r.viral_potential_analysis?.viral_potential_score || 0), 0) / (recommendationsList.length || 1);
        const shareRate = recommendationsList.reduce((acc, r) => acc + (r.predictive_performance_metrics?.estimated_share_rate_percent || 0), 0) / (recommendationsList.length || 1);
        
        return {
            score: (viralScore * 100).toFixed(0),
            share: shareRate.toFixed(2),
            level: viralScore > 0.6 ? "High Velocity" : viralScore > 0.4 ? "Moderate Spread" : "Organic Growth"
        };
    };
    const viral = getViralMetrics();

    // --- 3. DATA SYNTHESIS: Memory & Attention ---
    const calculateMemoryImpact = () => {
        const avgMem = recommendationsList.reduce((acc, r) => acc + (r.memory_encoding_prediction?.memory_encoding_strength || 0), 0) / (recommendationsList.length || 1);
        
        if (avgMem > 0.6) return { label: "Deep Encoding", desc: "Assets create strong long-term memories. Good for name recognition.", color: "text-purple-400", bar: "bg-purple-500", val: avgMem };
        if (avgMem > 0.4) return { label: "Standard Recall", desc: "Standard retention rates. Requires repetition to stick.", color: "text-blue-400", bar: "bg-blue-500", val: avgMem };
        return { label: "Fleeting Impact", desc: "High churn. Voters may engage but will forget quickly. Needs high frequency.", color: "text-slate-400", bar: "bg-slate-500", val: avgMem };
    };
    const memory = calculateMemoryImpact();

    // Attention Drivers
    const attentionDrivers = {};
    recommendationsList.forEach(r => {
        (r.visual_rhetoric_analysis?.attention_capture_analysis?.attention_drivers || []).forEach(driver => {
            const d = driver.split('(')[0].trim();
            attentionDrivers[d] = (attentionDrivers[d] || 0) + 1;
        });
    });
    const topAttentionDriver = Object.entries(attentionDrivers).sort(([,a], [,b]) => b - a)[0];

    // Boost Recommendations
    const boostRecs = {};
    recommendationsList.forEach(r => {
        const rec = r.viral_potential_analysis?.boost_recommendation || "Unknown";
        boostRecs[rec] = (boostRecs[rec] || 0) + 1;
    });
    const topBoostRec = Object.entries(boostRecs).sort(([,a], [,b]) => b - a)[0];

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-8">
             
             {/* 1. CAMPAIGN IMPACT MODELING */}
             {persuasionMetrics && (
                <section>
                    <div className="bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl p-6">
                         <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-emerald-500/20 rounded-lg border border-emerald-500/30">
                                <TrendingUp size={20} className="text-emerald-400" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Campaign Impact Modeling</h3>
                                <p className="text-xs text-slate-400">Predicted voter behavior based on asset analysis</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* The "Why" Hook */}
                            <div className="bg-slate-900/50 p-5 rounded-xl border border-slate-700/30 relative overflow-hidden col-span-1 md:col-span-2">
                                <div className="relative z-10 flex flex-col justify-between h-full">
                                    <div>
                                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Primary Turnout Driver</div>
                                        <h2 className="text-2xl font-black text-white mb-2">{hook.title}</h2>
                                        <p className="text-sm text-slate-300 leading-relaxed">
                                            {hook.desc}
                                        </p>
                                    </div>
                                    <div className="mt-4 flex gap-4">
                                        <div className="text-xs text-emerald-400 flex items-center gap-1">
                                            <Zap size={12} /> Reward Signal: {(avgStriatum * 100).toFixed(0)}%
                                        </div>
                                        <div className="text-xs text-rose-400 flex items-center gap-1">
                                            <Activity size={12} /> Urgency Signal: {(avgInsula * 100 * 3).toFixed(0)}%
                                        </div>
                                    </div>
                                </div>
                                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>
                            </div>

                            {/* Uplift Stat */}
                            <div className="bg-slate-900/50 p-5 rounded-xl border border-slate-700/30 flex flex-col justify-center">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Engagement Lift</div>
                                <div className="text-4xl font-black font-mono text-emerald-400 mb-1">
                                    +{persuasionMetrics.avgUplift.toFixed(1)}%
                                </div>
                                <p className="text-[10px] text-slate-500">
                                    Projected increase in attention vs. generic campaign materials.
                                </p>
                            </div>
                        </div>

                        {/* Secondary Metrics Row */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            {/* Memory Stickiness */}
                            <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700/30">
                                <div className="flex items-center gap-2 mb-2">
                                    <BrainCircuit size={16} className={memory.color} />
                                    <span className={`text-xs font-bold ${memory.color} uppercase tracking-wider`}>Campaign Retention</span>
                                </div>
                                <div className="flex justify-between items-end mb-1">
                                    <span className="text-sm font-bold text-white">{memory.label}</span>
                                    <span className="text-[10px] font-mono text-slate-400">{(memory.val * 100).toFixed(0)}/100</span>
                                </div>
                                <div className="w-full bg-slate-800 rounded-full h-1 mb-2">
                                    <div className={`h-full rounded-full ${memory.bar}`} style={{width: `${memory.val * 100}%`}}></div>
                                </div>
                                <p className="text-[10px] text-slate-500 leading-tight">{memory.desc}</p>
                            </div>

                            {/* Attention Economics */}
                            <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700/30">
                                <div className="flex items-center gap-2 mb-2">
                                    <MousePointer2 size={16} className="text-blue-400" />
                                    <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">Attention Driver</span>
                                </div>
                                <div className="text-sm text-white font-bold mb-1 truncate" title={topAttentionDriver?.[0]}>
                                    "{topAttentionDriver ? topAttentionDriver[0] : 'N/A'}"
                                </div>
                                <p className="text-[10px] text-slate-500 leading-tight">
                                    The primary visual element stopping the scroll.
                                </p>
                            </div>

                            {/* Ad Spend Strategy */}
                            <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700/30">
                                <div className="flex items-center gap-2 mb-2">
                                    <Megaphone size={16} className="text-purple-400" />
                                    <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Spend Directive</span>
                                </div>
                                <div className="text-sm text-white font-bold mb-1">
                                    {topBoostRec ? topBoostRec[0] : 'Review Metrics'}
                                </div>
                                <p className="text-[10px] text-slate-500 leading-tight">
                                    {topBoostRec && topBoostRec[0].includes('Organic') ? 'Save budget for higher converting assets.' : 'High viral potential warrants paid boost.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
             )}

             {/* 2. [NEW SECTION] NEURO-PERSUASION ENGINE */}
             <section>
                <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="p-2 bg-purple-500/10 rounded-lg border border-purple-500/20">
                            <BrainCircuit size={20} className="text-purple-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-300">Neuro-Persuasion Engine</h3>
                            <p className="text-xs text-slate-400 font-medium">Subconscious drivers and cognitive load analysis</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        
                        {/* 1. Drive Balance (Reward vs Threat) */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 col-span-1 md:col-span-2">
                            <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-2">
                                    <Activity size={14} className="text-slate-400" />
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Motivation Balance</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 h-6 w-full rounded-full bg-slate-800 overflow-hidden mb-2 relative">
                                {/* Reward Bar */}
                                <div className="h-full bg-emerald-500 flex items-center justify-start pl-3 text-[9px] text-emerald-950 font-bold uppercase transition-all duration-1000" style={{ width: `${(avgStriatum / (avgStriatum + avgInsula || 1)) * 100}%` }}>
                                    Reward
                                </div>
                                {/* Threat Bar */}
                                <div className="h-full bg-rose-500 flex items-center justify-end pr-3 text-[9px] text-rose-950 font-bold uppercase transition-all duration-1000 flex-1">
                                    Threat
                                </div>
                                {/* Middle Marker */}
                                <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-900 z-10"></div>
                            </div>
                            <div className="flex justify-between text-[10px] font-mono text-slate-500">
                                <span>{(avgStriatum * 100).toFixed(0)}% Desire</span>
                                <span>{(avgInsula * 100 * 3).toFixed(0)}% Urgency</span>
                            </div>
                        </div>

                        {/* 2. Cognitive Load (Dwell Time) */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50">
                            <div className="flex items-center gap-2 mb-2">
                                <Timer size={14} className="text-blue-400" />
                                <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Cognitive Load</span>
                            </div>
                            <div className="text-2xl font-black text-white mb-1">
                                {avgDwell.toFixed(1)}s
                            </div>
                            <p className="text-[10px] text-slate-400 leading-tight font-medium">
                                {avgDwell > 4 ? "Deep Read. High information density." : avgDwell > 2 ? "Balanced. Good for social." : "Quick Glance. Needs bold headline."}
                            </p>
                        </div>

                        {/* 3. Memory Stickiness */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50">
                            <div className="flex items-center gap-2 mb-2">
                                <Activity size={14} className={memory.color} />
                                <span className={`text-[10px] font-bold ${memory.color} uppercase tracking-widest`}>Retention</span>
                            </div>
                            <div className="text-lg font-bold text-white mb-1 truncate">
                                {memory.label}
                            </div>
                            <div className="w-full bg-slate-800 rounded-full h-1 mb-2">
                                <div className={`h-full rounded-full ${memory.bar}`} style={{width: `${memory.val * 100}%`}}></div>
                            </div>
                            <p className="text-[10px] text-slate-400 leading-tight font-medium">
                                {memory.desc}
                            </p>
                        </div>

                    </div>
                </GlassContainer>
             </section>

             {/* 3. [NEW SECTION] VIRAL PHYSICS & CHANNEL STRATEGY */}
             <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                 
                 {/* LEFT: Viral Physics */}
                 <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                            <Rocket size={18} className="text-indigo-400" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Viral Physics</h3>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-950/30 p-3 rounded-lg border border-slate-800/50">
                            <div className="text-[9px] text-slate-500 uppercase font-bold mb-1">Virality Score</div>
                            <div className="text-2xl font-black text-white">{viral.score}/100</div>
                            <div className="text-[10px] text-indigo-400 font-bold mt-1">{viral.level}</div>
                        </div>
                        <div className="bg-slate-950/30 p-3 rounded-lg border border-slate-800/50">
                            <div className="text-[9px] text-slate-500 uppercase font-bold mb-1">Share Rate</div>
                            <div className="text-2xl font-black text-white">{viral.share}%</div>
                            <div className="text-[10px] text-slate-400 font-medium mt-1">Est. Forward Rate</div>
                        </div>
                        <div className="bg-slate-950/30 p-3 rounded-lg border border-slate-800/50 col-span-2">
                            <div className="flex items-center gap-2 mb-1">
                                <MousePointer2 size={12} className="text-amber-400" />
                                <span className="text-[9px] text-amber-400 uppercase font-bold">Top Attention Hook</span>
                            </div>
                            <div className="text-sm font-bold text-white truncate">"{topAttentionDriver?.[0] || 'N/A'}"</div>
                        </div>
                    </div>
                 </GlassContainer>

                 {/* RIGHT: Channel Matrix */}
                 <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                            <Target size={18} className="text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Channel Fit</h3>
                        </div>
                    </div>

                    <div className="space-y-2">
                        {['leaflet', 'social_media', 'website', 'email'].map(channel => {
                            const scores = recommendationsList.map(r => r.medium_compatibility_matrix?.[channel]?.suitability_score || 0);
                            const avgScore = scores.reduce((a,b) => a+b, 0) / (scores.length || 1);
                            
                            // Updated Icons to fix crash (HandMouse -> FileText)
                            const icons = { leaflet: FileText, social_media: Share2, website: Eye, email: Mail };
                            const Icon = icons[channel] || Target;
                            
                            // Color Logic
                            let color = "bg-slate-500";
                            let text = "text-slate-400";
                            if (avgScore > 0.75) { color = "bg-emerald-500"; text = "text-emerald-400"; }
                            else if (avgScore > 0.5) { color = "bg-blue-500"; text = "text-blue-400"; }
                            else if (avgScore > 0.3) { color = "bg-amber-500"; text = "text-amber-400"; }

                            return (
                                <div key={channel} className="flex items-center justify-between p-2 rounded hover:bg-slate-800/50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <Icon size={14} className="text-slate-500" />
                                        <span className="text-xs font-bold text-slate-300 capitalize">{channel.replace('_', ' ')}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                            <div className={`h-full rounded-full ${color}`} style={{ width: `${avgScore * 100}%` }}></div>
                                        </div>
                                        <span className={`text-xs font-mono font-bold w-8 text-right ${text}`}>{(avgScore * 100).toFixed(0)}%</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                 </GlassContainer>
             </section>

             {/* 4. MESSAGE COHERENCE (RESTORED) */}
             {messageConsistency && (
                <section>
                    <GlassContainer className="p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-cyan-500/20 rounded-lg border border-cyan-500/30">
                                <MessageSquare size={20} className="text-cyan-400" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Message Coherence</h3>
                                <p className="text-xs text-slate-400">Thematic and ideological consistency analysis</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Overall Consistency Score & Themes */}
                            <div className="space-y-4">
                                <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/30">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-sm font-semibold text-white">Consistency Score</span>
                                        <span className={`text-2xl font-black font-mono ${
                                            messageConsistency.overall_consistency_score > 0.7 ? 'text-emerald-400' :
                                            messageConsistency.overall_consistency_score > 0.5 ? 'text-yellow-400' :
                                            'text-red-400'
                                        }`}>
                                            {Math.round(messageConsistency.overall_consistency_score * 100)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-slate-700/50 rounded-full h-2 overflow-hidden">
                                        <div 
                                            className={`h-full transition-all duration-1000 ${
                                                messageConsistency.overall_consistency_score > 0.7 ? 'bg-emerald-500' :
                                                messageConsistency.overall_consistency_score > 0.5 ? 'bg-yellow-500' :
                                                'bg-red-500'
                                            }`}
                                            style={{ width: `${messageConsistency.overall_consistency_score * 100}%` }}
                                        />
                                    </div>
                                    <div className="text-xs text-slate-400 mt-2">
                                        {messageConsistency.consistency_assessment}
                                    </div>
                                </div>
                                {/* Dominant Themes */}
                                <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/30">
                                    <h4 className="text-sm font-semibold text-white mb-3">Recurring Themes</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {(messageConsistency.dominant_themes || []).map((theme, i) => (
                                            <span key={i} className="px-3 py-1 bg-indigo-900/30 text-indigo-300 text-xs rounded-full border border-indigo-800/50 font-medium capitalize">
                                                {theme.replace(/_/g, ' ')}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Frame Consistency Breakdown */}
                            <div className="space-y-3">
                                {/* Ideological Consistency */}
                                {messageConsistency.ideological_consistency && (
                                    <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/30">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-xs text-slate-400 uppercase tracking-wider">Ideological Frame</span>
                                            <span className="text-sm font-mono font-bold text-white">
                                                {Math.round(messageConsistency.ideological_consistency.score * 100)}%
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-300">
                                            Dominant: <span className="text-white font-semibold capitalize">
                                                {messageConsistency.ideological_consistency.dominant_frame.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {/* Temporal Consistency */}
                                {messageConsistency.temporal_consistency && (
                                    <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/30">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-xs text-slate-400 uppercase tracking-wider">Temporal Frame</span>
                                            <span className="text-sm font-mono font-bold text-white">
                                                {Math.round(messageConsistency.temporal_consistency.score * 100)}%
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-300">
                                            Dominant: <span className="text-white font-semibold capitalize">
                                                {messageConsistency.temporal_consistency.dominant_frame.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                         {/* Strategic Recommendation */}
                         {messageConsistency.recommendation && (
                            <div className="mt-6 pt-4 border-t border-slate-700/50">
                                <div className="flex items-start gap-3 bg-slate-900/40 p-4 rounded-lg border border-slate-700/30">
                                    <Lightbulb size={16} className="text-amber-400 mt-0.5" />
                                    <div>
                                        <div className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-1">Consistency Recommendation</div>
                                        <p className="text-sm text-slate-300 leading-relaxed">
                                            {messageConsistency.recommendation}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </GlassContainer>
                </section>
            )}

            {/* 5. EMOTIONAL LANDSCAPE (RESTORED) */}
             {recommendationsList.length > 0 && (
                <section>
                    <GlassContainer className="p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-rose-500/20 rounded-lg border border-rose-500/30">
                                <Heart size={20} className="text-rose-400" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Emotional Landscape</h3>
                                <p className="text-xs text-slate-400">Aggregate emotional tone and valence distribution</p>
                            </div>
                        </div>
                        {(() => {
                            // Calculate emotional aggregates
                            const emotions = {};
                            const valenceSum = recommendationsList.reduce((sum, img) => {
                                const emotion = img.dominant_emotion || 'neutral';
                                emotions[emotion] = (emotions[emotion] || 0) + 1;
                                return sum + (img.avg_valence || 0);
                            }, 0);
                            
                            const arousalSum = recommendationsList.reduce((sum, img) => 
                                sum + (img.avg_arousal || 0), 0
                            );
                            
                            const avgValence = valenceSum / recommendationsList.length;
                            const avgArousal = arousalSum / recommendationsList.length;
                            
                            const sortedEmotions = Object.entries(emotions)
                                .sort(([,a], [,b]) => b - a)
                                .slice(0, 5);

                            return (
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Valence/Arousal Quadrant */}
                                    <div className="bg-gradient-to-br from-rose-900/20 to-slate-900/50 p-5 rounded-xl border border-rose-500/20">
                                        <h4 className="text-sm font-bold text-rose-300 uppercase tracking-wider mb-4">Emotional Positioning</h4>
                                        
                                        <div className="space-y-4">
                                            {/* Valence */}
                                            <div>
                                                <div className="flex justify-between text-xs mb-2">
                                                    <span className="text-slate-400">Valence (Pleasure)</span>
                                                    <span className={`font-mono font-bold ${avgValence > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                        {avgValence > 0 ? '+' : ''}{avgValence.toFixed(2)}
                                                    </span>
                                                </div>
                                                <div className="relative h-2 w-full bg-slate-700/50 rounded-full overflow-hidden">
                                                    <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-500 z-10" />
                                                    <div 
                                                        className={`absolute h-full ${avgValence > 0 ? 'bg-green-500' : 'bg-red-500'}`}
                                                        style={{ 
                                                            left: avgValence < 0 ? `${50 + (avgValence * 50)}%` : '50%',
                                                            width: `${Math.abs(avgValence * 50)}%` 
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* Arousal */}
                                            <div>
                                                <div className="flex justify-between text-xs mb-2">
                                                    <span className="text-slate-400">Arousal (Energy)</span>
                                                    <span className="font-mono font-bold text-white">
                                                        {(avgArousal * 100).toFixed(0)}%
                                                    </span>
                                                </div>
                                                <div className="h-2 w-full bg-slate-700/50 rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full bg-orange-500 rounded-full transition-all duration-1000"
                                                        style={{ width: `${avgArousal * 100}%` }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Emotional Classification */}
                                            <div className="bg-slate-900/50 p-3 rounded border border-slate-700/30 mt-4">
                                                <div className="text-xs text-slate-500 mb-1">Overall Tone</div>
                                                <div className="text-lg font-bold text-white">
                                                    {avgValence > 0.2 && avgArousal > 0.6 ? 'Excited & Positive' :
                                                     avgValence > 0.2 && avgArousal <= 0.6 ? 'Calm & Content' :
                                                     avgValence <= 0.2 && avgArousal > 0.6 ? 'Intense & Urgent' :
                                                     'Subdued & Serious'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Emotion Distribution */}
                                    <div className="bg-slate-900/50 p-5 rounded-xl border border-slate-700/30">
                                        <h4 className="text-sm font-bold text-white mb-4">Emotion Distribution</h4>
                                        <div className="space-y-3">
                                            {sortedEmotions.map(([emotion, count]) => {
                                                const percentage = (count / recommendationsList.length) * 100;
                                                const emotionColors = {
                                                    joy: 'bg-yellow-500',
                                                    trust: 'bg-green-500',
                                                    fear: 'bg-purple-500',
                                                    surprise: 'bg-cyan-500',
                                                    sadness: 'bg-blue-500',
                                                    anger: 'bg-red-500',
                                                    anticipation: 'bg-orange-500',
                                                    disgust: 'bg-pink-500',
                                                    neutral: 'bg-slate-500'
                                                };
                                                const color = emotionColors[emotion.toLowerCase()] || 'bg-slate-500';
                                                
                                                return (
                                                    <div key={emotion}>
                                                        <div className="flex justify-between text-xs mb-1.5">
                                                            <span className="text-slate-300 capitalize">{emotion}</span>
                                                            <span className="font-mono text-white">{count} ({percentage.toFixed(1)}%)</span>
                                                        </div>
                                                        <div className="h-2 w-full bg-slate-700/50 rounded-full overflow-hidden">
                                                            <div 
                                                                className={`h-full ${color} rounded-full transition-all duration-1000`}
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Strategic Implications */}
                                    <div className="bg-slate-900/50 p-5 rounded-xl border border-slate-700/30">
                                        <h4 className="text-sm font-bold text-white mb-4">Strategic Implications</h4>
                                        <div className="space-y-3">
                                            <div className="bg-slate-800/50 p-3 rounded">
                                                <div className="text-xs text-slate-500 mb-2">Recommended Pairing</div>
                                                <p className="text-sm text-slate-200">
                                                    {avgValence > 0 && avgArousal > 0.5 
                                                        ? "High-energy messaging with aspirational calls to action"
                                                        : avgValence > 0 
                                                        ? "Reassuring messaging emphasizing stability and trust"
                                                        : avgArousal > 0.5
                                                        ? "Urgent messaging highlighting stakes and consequences"
                                                        : "Thoughtful messaging focusing on deliberation and reason"}
                                                </p>
                                            </div>
                                            
                                            <div className="bg-slate-800/50 p-3 rounded">
                                                <div className="text-xs text-slate-500 mb-2">Audience Resonance</div>
                                                <p className="text-sm text-slate-200">
                                                    {avgValence > 0 
                                                        ? "Best for engaging optimistic, forward-looking voters"
                                                        : "Effective for threat-sensitive, security-focused audiences"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </GlassContainer>
                </section>
            )}

            {/* 6. STRATEGIC GAPS (Grid Layout) */}
             <section>
                <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="p-2 bg-rose-500/10 rounded-lg border border-rose-500/20">
                            <AlertTriangle size={20} className="text-rose-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-300">Strategic Gaps</h3>
                            <p className="text-xs text-slate-400 font-medium">Critical missing elements for conversion</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {strategicGaps && strategicGaps.length > 0 ? strategicGaps.map((gap, i) => (
                            <div key={i} className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex items-start gap-3">
                                <div className="mt-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]"></div>
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-white mb-1 uppercase tracking-wide">Gap Detected</div>
                                    <p className="text-sm text-slate-400 leading-relaxed font-medium">
                                        {gap}
                                    </p>
                                </div>
                            </div>
                        )) : (
                            <div className="col-span-2 bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-xl flex items-center justify-center gap-2">
                                <CheckCircle2 size={16} className="text-emerald-400" />
                                <span className="text-sm font-bold text-emerald-300">No critical strategic gaps detected.</span>
                            </div>
                        )}
                    </div>
                </GlassContainer>
            </section>
        </div>
    );
};